import { Box, Stack, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./bookings.styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const MetricCard = ({ icon, iconBg, iconColor, title, value, subtitle, trend, trendType, theme }) => {
  const colors = getColors(theme);

  const getTrendIcon = () => {
    if (trendType === "up" || trendType === "success") {
      return <TrendingUpIcon sx={{ fontSize: 14, color: colors.success }} />;
    }
    if (trendType === "down" || trendType === "danger") {
      return <TrendingDownIcon sx={{ fontSize: 14, color: colors.danger }} />;
    }
    return null;
  };

  const getTrendColor = () => {
    if (trendType === "up" || trendType === "success") return colors.success;
    if (trendType === "down" || trendType === "danger") return colors.danger;
    return colors.textSecondary;
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: shadows.cardHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Row 1: Title (left) + Icon (right) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: colors.textMuted,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Stack>

      {/* Row 2: Bold value */}
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: colors.textPrimary,
          mb: 1,
          lineHeight: 1.2,
        }}
      >
        {value ?? "--"}
      </Typography>

      {/* Row 3: Trend or subtitle */}
      {trend && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {getTrendIcon()}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: getTrendColor(),
            }}
          >
            {trend}
          </Typography>
        </Stack>
      )}

      {subtitle && !trend && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: colors.textSecondary,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

const BookingMetrics = ({ data }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  const metrics = [
    {
      icon: <EventAvailableIcon sx={{ fontSize: 20, color: primaryColor }} />,
      iconBg: colors.primaryAlpha(10),
      title: "TOTAL BOOKINGS",
      value: data?.totalBookings ? data.totalBookings.toLocaleString() : null,
      trend: data?.bookingsTrend,
      trendType: data?.bookingsTrendType,
    },
    {
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20, color: colors.success }} />,
      iconBg: colors.successLight,
      title: "SHOW RATE",
      value: data?.showRate ? `${data.showRate}%` : null,
      trend: data?.showRateTrend,
      trendType: data?.showRateTrendType,
    },
    {
      icon: <CancelOutlinedIcon sx={{ fontSize: 20, color: colors.danger }} />,
      iconBg: colors.dangerLight,
      title: "CANCELLATIONS",
      value: data?.cancellations ? `${data.cancellations}%` : null,
      trend: data?.cancellationsTrend,
      trendType: data?.cancellationsTrendType,
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 20, color: colors.warning }} />,
      iconBg: colors.warningLight,
      title: "AVG. LEAD TO BOOKING",
      value: data?.avgLeadToBooking ? `${data.avgLeadToBooking} Days` : null,
      trend: data?.avgLeadTrend,
      trendType: data?.avgLeadTrendType,
    },
  ];

  return (
    <Grid container spacing={3}>
      {metrics.map((metric, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <MetricCard {...metric} theme={theme} />
        </Grid>
      ))}
    </Grid>
  );
};

export default BookingMetrics;
